import type { z } from 'zod';
import { getClassLabel } from '../classes/codecs';
import { type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { DBLessonEnrolmentSchema, LessonEnrolmentSchema } from './schemas';

type DBLessonEnrolment = z.infer<typeof DBLessonEnrolmentSchema>;
type LessonEnrolment = z.infer<typeof LessonEnrolmentSchema>;

export const LessonEnrolmentCodecConfig: TransformerConfig<
  typeof LessonEnrolmentSchema,
  typeof DBLessonEnrolmentSchema,
  'id' | 'lessonId' | 'classId' | 'classLabel'
> = {
  name: 'LessonEnrolment',
  clientSchema: LessonEnrolmentSchema,
  dbSchema: DBLessonEnrolmentSchema,
  transformers: [
    createRenameTransformer<typeof LessonEnrolmentSchema, typeof DBLessonEnrolmentSchema>(
      'studentId',
      'student'
    ),

    // subject, classCode, courseId, lessonTemplateCode, counter ↔ classLessonIdentifier:
    // 'subject#classCode#courseId#lessonTemplateCode#counter', and the course again as `course`,
    // which keys an index
    createTransformer<typeof LessonEnrolmentSchema, typeof DBLessonEnrolmentSchema>({
      encoder: {
        sourceFields: [
          'subject',
          'classCode',
          'courseId',
          'lessonTemplateCode',
          'counter',
        ] as const,
        dependencies: [
          'subject',
          'classCode',
          'courseId',
          'lessonTemplateCode',
          'counter',
        ] as const,
        encode: ({ subject, classCode, courseId, lessonTemplateCode, counter }) => ({
          classLessonIdentifier: `${subject}#${classCode}#${courseId}#${lessonTemplateCode}#${counter}`,
          course: courseId as LessonEnrolment['courseId'],
        }),
      },
      decoder: {
        sourceFields: ['classLessonIdentifier', 'course'] as const,
        decode: ({ classLessonIdentifier, course }) => {
          if (classLessonIdentifier === undefined) {
            return course === undefined ? {} : { courseId: course };
          }
          const parts = (classLessonIdentifier as DBLessonEnrolment['classLessonIdentifier']).split(
            '#'
          );
          return {
            subject: parts[0] as Subject,
            classCode: parts[1] as string,
            courseId: parts[2] as string,
            lessonTemplateCode: parts[3] as string,
            counter: parts[4] as string,
          };
        },
      },
    }),

    // rollMarked: boolean ↔ rollMarked: 'true' | 'false'
    createTransformer<typeof LessonEnrolmentSchema, typeof DBLessonEnrolmentSchema>({
      encoder: {
        sourceFields: ['rollMarked'] as const,
        encode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as LessonEnrolment['rollMarked']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['rollMarked'] as const,
        decode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as DBLessonEnrolment['rollMarked']) === 'true',
        }),
      },
    }),

    createRenameTransformer<typeof LessonEnrolmentSchema, typeof DBLessonEnrolmentSchema>(
      'streamId',
      'stream'
    ),
  ],
  derived: {
    id: enrolment => buildRecordId('lessonEnrolment', enrolment),
    lessonId: enrolment => buildRecordId('lesson', enrolment),
    classId: enrolment =>
      buildRecordId('class', { subject: enrolment.subject, code: enrolment.classCode }),
    classLabel: enrolment =>
      enrolment.courseId !== undefined && enrolment.classCode !== undefined
        ? getClassLabel({ courseId: enrolment.courseId, code: enrolment.classCode })
        : undefined,
  },
};

export const encodeLessonEnrolmentToDb = createEncodeToDbFn(LessonEnrolmentCodecConfig);
export const decodeLessonEnrolmentFromDb = createDecodeFromDbFn(LessonEnrolmentCodecConfig);
